import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { Params, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ShPopoverComponent, ShTagComponent } from '@seahorse/components';
import type { ActionMenuItem } from '@seahorse/ui';
import { ActionBarComponent } from '../action-bar/action-bar.component';
import { PageAction } from '../page-header/page-header.models';
import {
  DetailStatus,
  KeyFact,
  MAX_KEY_FACTS,
  hiddenFactIndexes,
} from './detail-header.models';

/**
 * The generic detail-page header (design handoff, "Step 7, streamlined: the
 * detail page"): back link, record title with a status tag and subline,
 * actions (the same `PageAction` model and `sh-action-bar` as the page
 * header and the peek), up to five key facts, and a slot for the page's own
 * tab bar. It is fed with data and never knows the entity.
 *
 * The page decides the back link (route plus query params, so the list
 * comes back with its filters). The tab bar is an `sh-tabs` projected through
 * `[detailTabs]`; the page renders its `sh-tab-panels` in the body.
 *
 * Styling lives in `@seahorse/theme`'s `_detail.scss`.
 */
@Component({
  selector: 'sh-detail-header',
  // Default, not OnPush: the `sh-action-bar` evaluates action
  // `visible`/`disabled` functions over page state; an OnPush host with
  // unchanged inputs would skip that subtree and leave them stale.
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    RouterLink,
    TranslateModule,
    ShTagComponent,
    ShPopoverComponent,
    ActionBarComponent,
  ],
  templateUrl: './detail-header.component.html',
})
export class DetailHeaderComponent implements OnDestroy {
  /** Translation key or plain text. */
  backLabel = input('');
  backLink = input<string | unknown[]>('/');
  backQueryParams = input<Params>();
  /**
   * Runs instead of navigating to `backLink` when set, so the page can guard
   * the way out (e.g. "Discard changes?"). The link is then not a routerLink.
   */
  backCommand = input<() => void>();

  title = input('');
  status = input<DetailStatus>();
  subline = input<string>();

  primaryAction = input<PageAction>();
  secondaryActions = input<PageAction[]>();
  overflowActions = input<ActionMenuItem[]>();

  /**
   * Key facts, most important first. Facts are hidden from the end when they
   * don't fit the header's width, and remain reachable through the "+N" popover.
   */
  facts = input<KeyFact[]>();

  // A computed with default (reference) equality: it only produces a new
  // array when the `facts` input itself changes, not on every change
  // detection, so the *ngFor below never re-creates its rows.
  protected readonly visibleFacts = computed(() =>
    (this.facts() ?? []).slice(0, MAX_KEY_FACTS)
  );

  /** The facts that wrapped out of sight, in order. Only replaced when the set really changes. */
  protected readonly hiddenFacts = signal<KeyFact[]>([]);

  private _factsRow?: ElementRef<HTMLElement>;
  private _observer?: ResizeObserver;
  private _frame?: number;

  constructor(private readonly _zone: NgZone) {
    // New facts: measure again once they are rendered.
    effect(() => {
      this.visibleFacts();
      this._scheduleMeasure();
    });
  }

  /** The facts row lives inside an *ngIf: (re)attach the observer whenever it appears or goes. */
  @ViewChild('factsRow')
  protected set factsRow(ref: ElementRef<HTMLElement> | undefined) {
    this._observer?.disconnect();
    this._observer = undefined;
    this._factsRow = ref;
    if (ref && typeof ResizeObserver !== 'undefined') {
      // Outside Angular: only a real change of the hidden set re-enters the zone.
      this._zone.runOutsideAngular(() => {
        this._observer = new ResizeObserver(() => this._scheduleMeasure());
        this._observer.observe(ref.nativeElement);
      });
    }
    this._scheduleMeasure();
  }

  ngOnDestroy(): void {
    this._observer?.disconnect();
    if (
      this._frame !== undefined &&
      typeof cancelAnimationFrame !== 'undefined'
    ) {
      cancelAnimationFrame(this._frame);
    }
  }

  private _scheduleMeasure(): void {
    if (
      this._frame !== undefined ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      return;
    }
    this._zone.runOutsideAngular(() => {
      this._frame = requestAnimationFrame(() => {
        this._frame = undefined;
        this.measure();
      });
    });
  }

  /** Reads the layout and updates `hiddenFacts` when (and only when) the hidden set changed. */
  measure(): void {
    const row = this._factsRow?.nativeElement;
    let hidden: KeyFact[] = [];
    if (row) {
      const facts = this.visibleFacts();
      const tops = Array.from(
        row.querySelectorAll<HTMLElement>('.detail-fact')
      ).map((el) => el.offsetTop);
      hidden = hiddenFactIndexes(tops, row.clientHeight)
        .map((i) => facts[i])
        .filter(Boolean);
    }
    const current = this.hiddenFacts();
    if (
      hidden.length === current.length &&
      hidden.every((f, i) => f === current[i])
    ) {
      return;
    }
    this._zone.run(() => this.hiddenFacts.set(hidden));
  }

  protected hasActions(): boolean {
    return (
      !!this.primaryAction() ||
      (this.secondaryActions()?.length ?? 0) > 0 ||
      (this.overflowActions()?.length ?? 0) > 0
    );
  }

  protected readonly trackByFactLabel = (_: number, fact: KeyFact) =>
    fact.label;
}
